import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { sitesFromAccess } from '@/components/patients/roles/referrerPatientMapper';
import type { ReferrerProfileSite } from '@/components/patients/roles/referrerPatientTypes';
import {
  fetchMySites,
  updateMySiteColor,
  type UserSiteAccessItem,
} from '@/services/userSites';
import { resolveSiteColor } from '@/utils/siteColorStyle';
import { useAuthStore } from '@/store/auth';

const MIN_SYNC_INTERVAL_MS = 8_000;
const SITE_PRIVILEGES = ['ACCESS_SITES', 'ACCESS_RADFLOW'] as const;

export const useUserSitesStore = defineStore('userSites', () => {
  const items = ref<UserSiteAccessItem[]>([]);
  const userSerial = ref('');
  const grantedCount = ref(0);
  const blockedCount = ref(0);
  const activeUserId = ref<number | undefined>(undefined);
  const syncing = ref(false);
  const lastSyncError = ref<string | null>(null);

  let syncPromise: Promise<void> | null = null;
  let lastSyncAt = 0;

  const profileSites = computed<ReferrerProfileSite[]>(() => sitesFromAccess(items.value));

  function canAccessSites(): boolean {
    const auth = useAuthStore();
    return SITE_PRIVILEGES.some((code) => auth.hasPrivilege(code));
  }

  function resolveUserId(): number | undefined {
    const authUserId = useAuthStore().user?.id;
    return activeUserId.value ?? authUserId;
  }

  function reset() {
    items.value = [];
    userSerial.value = '';
    grantedCount.value = 0;
    blockedCount.value = 0;
    lastSyncError.value = null;
    lastSyncAt = 0;
    syncPromise = null;
  }

  function applyServerResponse(data: {
    user_serial: string;
    items: UserSiteAccessItem[];
    granted: number;
    blocked: number;
  }) {
    items.value = data.items;
    userSerial.value = data.user_serial;
    grantedCount.value = data.granted;
    blockedCount.value = data.blocked;
  }

  function getColor(nodeId: string): string {
    const item = items.value.find((s) => s.node_id === nodeId);
    return resolveSiteColor(item?.color, nodeId);
  }

  async function syncFromServer(options: { force?: boolean } = {}) {
    const userId = resolveUserId();
    if (!userId) {
      reset();
      return;
    }

    if (!canAccessSites()) {
      reset();
      return;
    }

    const now = Date.now();
    if (
      !options.force
      && syncPromise === null
      && now - lastSyncAt < MIN_SYNC_INTERVAL_MS
      && items.value.length > 0
    ) {
      return;
    }

    if (syncPromise) {
      return syncPromise;
    }

    activeUserId.value = userId;
    syncing.value = true;
    lastSyncError.value = null;

    syncPromise = (async () => {
      try {
        const data = await fetchMySites();
        applyServerResponse(data);
        lastSyncAt = Date.now();
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        lastSyncError.value = message || 'No se pudieron cargar las sedes.';
      } finally {
        syncing.value = false;
        syncPromise = null;
      }
    })();

    return syncPromise;
  }

  function hydrate(userId?: number) {
    activeUserId.value = userId;
    if (!userId) {
      reset();
      return;
    }
    void syncFromServer({ force: true });
  }

  async function updateSiteColor(nodeId: string, color: string) {
    const saved = await updateMySiteColor(nodeId, color);
    items.value = items.value.map((item) =>
      item.node_id === nodeId ? { ...item, color: saved.color } : item,
    );
    return saved;
  }

  return {
    items,
    userSerial,
    grantedCount,
    blockedCount,
    profileSites,
    syncing,
    lastSyncError,
    hydrate,
    syncFromServer,
    updateSiteColor,
    getColor,
    reset,
  };
});
